"use client";

import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect } from "react";

import { Menu } from "@/app/(application)/components/sidebar/Menu";
import { LogoLink } from "@/components/LogoLink";
import { useOpenModalCount } from "@/lib/zustand/useOpenModalCount";
import { useSidebar } from "@/lib/zustand/useSidebar";
import { UserNoDates } from "@/types/types";

export function OverlaySidebar({
  user,
  hideTimeLeft,
  showCovers,
  hasTtvAccess,
}: {
  user: UserNoDates | null;
  hideTimeLeft: boolean;
  showCovers: boolean;
  hasTtvAccess: boolean;
}) {
  const open = useSidebar((state) => state.open);
  const setClose = useSidebar((state) => state.setClose);
  const registerOpenModal = useOpenModalCount(
    (state) => state.registerOpenModal
  );
  const unregisterOpenModal = useOpenModalCount(
    (state) => state.unregisterOpenModal
  );

  // The open flag lives in a module-level store that outlives this component, and the panel's logo link navigates to / — outside the (application) layout — so the sidebar unmounts while still open. Without this reset the overlay would reopen on top of the next app page the user visits.
  useEffect(() => {
    return () => {
      setClose();
    };
  }, [setClose]);

  // This raw headlessui Dialog opens over the desktop TTV editor (the burger lives in its toolbar), so it must join the open-modal count like the shared Modal does: otherwise the document-level TTV shortcuts (Delete, Cmd+Z, Space, ...) keep acting on the score behind the overlay — and their Escape hotkey preventDefaults the keydown before headlessui's window listener, so Escape can't even close the sidebar.
  useEffect(() => {
    if (!open) {
      return;
    }
    registerOpenModal();
    return () => unregisterOpenModal();
  }, [open, registerOpenModal, unregisterOpenModal]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setClose}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={setClose}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 py-4">
                <LogoLink />

                <Menu
                  clickHandler={setClose}
                  user={user}
                  hideTimeLeft={hideTimeLeft}
                  className="flex-grow"
                  showCovers={showCovers}
                  hasTtvAccess={hasTtvAccess}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
